/**
 * Widget Constants
 *
 * Centralized configuration values for the PromptMySite widget.
 * Magic numbers should be defined here with documentation explaining
 * why each value was chosen.
 */

// =============================================================================
// NETWORK & TIMEOUTS
// =============================================================================

/**
 * Default request timeout in milliseconds (30 seconds).
 * Long enough for AI responses which can take 10-20s on complex queries,
 * but not so long that users wait forever on network issues.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

/**
 * Health check timeout in milliseconds (10 seconds).
 * Shorter than default since health checks should be fast.
 */
export const HEALTH_CHECK_TIMEOUT_MS = 10000;

/**
 * API discovery report interval in milliseconds (5 seconds).
 * Balances timely discovery with avoiding too frequent reports.
 */
export const API_DISCOVERY_INTERVAL_MS = 5000;

// =============================================================================
// UI TIMING
// =============================================================================

/**
 * Delay before rendering streamed content in milliseconds (30ms).
 * Provides smooth visual updates without excessive re-renders.
 * Lower = more responsive but more CPU. Higher = batched but laggy.
 */
export const STREAM_RENDER_DELAY_MS = 30;

/**
 * Loading indicator delay in milliseconds (150ms).
 * Shows loading state only if response takes longer than this.
 * Prevents flashing for fast responses.
 */
export const LOADING_INDICATOR_DELAY_MS = 150;

/**
 * Copy button feedback duration in milliseconds (2 seconds).
 * How long the "Copied!" state shows before reverting.
 */
export const COPY_FEEDBACK_DURATION_MS = 2000;

/**
 * State change reset delay in milliseconds (1 second).
 * Used for debouncing state change events in stores.
 */
export const STATE_CHANGE_RESET_DELAY_MS = 1000;

/**
 * State emission delay in milliseconds (2 seconds).
 * Delay before emitting reset events after state changes.
 */
export const STATE_EMIT_DELAY_MS = 2000;

/**
 * Click spam protection threshold in milliseconds (300ms).
 * Minimum time between repeated action clicks to prevent double-clicks.
 */
export const CLICK_DEBOUNCE_MS = 300;

// =============================================================================
// CONTENT LIMITS
// =============================================================================

/**
 * Maximum content length in characters (50KB).
 * Prevents memory issues from excessively long responses.
 */
export const MAX_CONTENT_LENGTH = 50000;

/**
 * Maximum JSON repair length in characters (50KB).
 * Limit for attempting to repair malformed JSON.
 */
export const MAX_JSON_REPAIR_LENGTH = 50000;

/**
 * Maximum message length in characters (4KB).
 * Reasonable limit for user input that won't overwhelm the API.
 */
export const MAX_MESSAGE_LENGTH = 4000;

/**
 * Analytics preview truncation length in characters.
 * How much of messages/responses to include in analytics events.
 */
export const ANALYTICS_PREVIEW_LENGTH = 100;

// =============================================================================
// RATE LIMITING
// =============================================================================

/**
 * Maximum messages per rate limit window.
 */
export const RATE_LIMIT_MAX_MESSAGES = 10;

/**
 * Rate limit window duration in milliseconds (1 minute).
 */
export const RATE_LIMIT_WINDOW_MS = 60 * 1000;

/**
 * Minimum interval between messages in milliseconds (500ms).
 * Prevents rapid-fire message spam.
 */
export const MIN_MESSAGE_INTERVAL_MS = 500;

// =============================================================================
// Z-INDEX LAYERS
// =============================================================================

/**
 * Z-index for the chat bubble (below panel).
 * High enough to float above page content but below modals.
 */
export const Z_INDEX_BUBBLE = 999998;

/**
 * Z-index for the chat panel.
 * Above the bubble, same layer as modals.
 */
export const Z_INDEX_PANEL = 999999;

/**
 * Z-index for overlay backdrop.
 * Below the panel but above page content.
 */
export const Z_INDEX_BACKDROP = 999997;

// =============================================================================
// STREAMING BUFFER
// =============================================================================

/**
 * Minimum characters before flushing buffer.
 *
 * 5 characters is typically one word or meaningful chunk.
 * This prevents the jarring "typewriter" effect of character-by-character
 * rendering while keeping content flowing naturally.
 *
 * Trade-offs:
 * - Lower (1-2): More responsive but jarring, high CPU from frequent DOM updates
 * - Higher (10+): Smoother batches but noticeably chunky delivery
 * - 5: Sweet spot - natural word-level updates
 */
export const STREAM_BUFFER_MIN_CHARS = 5;

/**
 * Maximum buffer wait time in milliseconds.
 *
 * 30ms ≈ 2 frames at 60fps - imperceptible to users.
 * Ensures content appears even with slow token delivery.
 *
 * Trade-offs:
 * - Lower (10ms): Very responsive but more timer overhead
 * - Higher (100ms): Noticeable lag in content appearance
 * - 30ms: Invisible delay, good batching, minimal overhead
 */
export const STREAM_BUFFER_MAX_WAIT_MS = 30;

// =============================================================================
// UI THRESHOLDS
// =============================================================================

/**
 * Badge count limit display threshold.
 * Show "9+" for counts above this to fit in badge UI.
 */
export const BADGE_COUNT_LIMIT = 9;

/**
 * Block processing threshold for nesting protection.
 * Maximum nesting depth before stopping to prevent crashes.
 */
export const MAX_BLOCK_NESTING_DEPTH = 5;

/**
 * Maximum blocks per message for crash protection.
 */
export const MAX_BLOCKS_PER_MESSAGE = 100;

/**
 * Block processing timeout in milliseconds (15 seconds).
 * Maximum time to spend processing blocks before giving up.
 */
export const BLOCK_PROCESSING_TIMEOUT_MS = 15000;

/**
 * Bubble pulse animation duration in milliseconds (1 second).
 * How long the attention-grabbing pulse animation runs.
 */
export const BUBBLE_PULSE_DURATION_MS = 1000;
